import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import { Link, useSearchParams } from "react-router-dom";
import moment from "moment";
import { ORDERS } from "../../utils/queries";
import toast from "react-hot-toast";

const formatter = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
});

export default function Orders(props) {
  const { vendor } = props;

  function statusTagColor(str) {
    if (str === "DRAFT") return `bg-warning`;
    if (str === "SUBMITTED") return `bg-info`;
    if (str === "FULFILLED") return `bg-success`;
    if (str === "RETURNED") return `bg-error`;
    return `bg-base-300`;
  }
  const [results, setResults] = useState([]);

  const [searchParams, setSearchParams] = useSearchParams();
  // normalize URL params early: page as number, search as string
  const search = searchParams.get("search") || "";
  const page = parseInt(searchParams.get("page") || "1", 10);
  const status = searchParams.get("status") || "ALL";

  const [displayPage, setDisplayPage] = useState(page);

  const [total, setTotal] = useState(0);
  const [limit] = useState(10);
  const [maxPage, setMaxPage] = useState(1);

  const debouncedTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debouncedTimerRef.current) {
        clearTimeout(debouncedTimerRef.current);
      }
    };
  }, []);

  const from = useMemo(() => {
    return total === 0 ? 0 : (displayPage - 1) * limit + 1;
  }, [displayPage, limit, total]);

  const to = useMemo(() => {
    return Math.min(displayPage * limit, total);
  }, [displayPage, limit, total]);

  const { loading } = useQuery(ORDERS, {
    variables: {
      status: status !== "ALL" ? status : null,
      vendor: vendor,
      page: page,
      limit: limit,
      // only send a trimmed non-empty search string
      search: search && search.trim() !== "" ? search.trim() : null,
    },
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      toast.dismiss();
      setResults(data.orders.list);
      setTotal(data.orders.totalOrders);
      setMaxPage(data.orders.totalPage);
      setDisplayPage(parseInt(page));
    },
    onError: (error) => {
      toast.dismiss();
      toast.error(error.message);
    },
  });

  useEffect(() => {
    if (loading) {
      toast.dismiss();
      toast.loading("Loading...");
    }
  }, [loading]);

  const handleSearch = useCallback(
    (e) => {
      const value = e.target.value;
      const keyword = value.trim();

      if (debouncedTimerRef.current) {
        clearTimeout(debouncedTimerRef.current);
      }

      debouncedTimerRef.current = setTimeout(() => {
        const nextPage = 1;

        if (!keyword) {
          setSearchParams({ page: String(nextPage) });
        } else {
          setSearchParams({ search: keyword, page: String(nextPage) });
        }
      }, 400);
    },
    [setSearchParams]
  );

  const handleStatusChange = useCallback(
    (e) => {
      const stat = e.target.value;
      const nextPage = 1;

      // Preserve search param if it exists
      const s = searchParams.get("search");
      const term = s && s.trim() ? s : null;

      const newParams = term
        ? {
            page: String(nextPage),
            search: s,
            status: stat,
            vendor: vendor,
          }
        : { page: String(nextPage), status: stat, vendor: vendor };
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams, vendor]
  );

  const nextPage = useCallback(() => {
    if (maxPage === 0) return; // No data yet
    if (displayPage >= maxPage) return; // Already on last page
    const target = displayPage + 1;

    // Preserve search param if it exists
    const s = searchParams.get("search");
    const term = s && s.trim() ? s : null;

    // Preserve status param if it exists
    const st = searchParams.get("status");
    const stat = st && st.trim() && st !== "ALL" ? st : null;

    const newParams = {
      ...(target && { page: String(target) }),
      ...(stat && { status: stat }),
      ...(vendor && { vendor: vendor }),
      ...(term && { search: s }),
    };
    setSearchParams(newParams);
  }, [maxPage, displayPage, searchParams, setSearchParams, vendor]);

  const prevPage = useCallback(() => {
    if (maxPage === 0) return; // No data yet
    if (displayPage <= 1) return; // Already on first page
    const target = displayPage - 1;

    // Preserve search param if it exists
    const s = searchParams.get("search");
    const term = s && s.trim() ? s : null;

    // Preserve status param if it exists
    const st = searchParams.get("status");
    const stat = st && st.trim() && st !== "ALL" ? st : null;

    const newParams = {
      ...(target && { page: String(target) }),
      ...(stat && { status: stat }),
      ...(vendor && { vendor: vendor }),
      ...(term && { search: s }),
    };

    setSearchParams(newParams);
  }, [maxPage, displayPage, searchParams, setSearchParams, vendor]);

  const statuses = ["DRAFT", "SUBMITTED", "FULFILLED", "CANCELLED", "RETURNED"];

  return (
    <div className="space-y-6">
      {/* table filter */}
      {results && (
        <div className="grid grid-flow-row xl:grid-flow-col px-2 gap-4">
          {/* search */}
          <div className="col-span-1">
            <input
              name="keyword"
              className="col-span-1 input input-bordered w-full max-w-md bg-white ring-primary-focus form-control"
              type="search"
              placeholder="Filter by Order No. / Ref."
              onChange={handleSearch}
              // controlled input so it updates when URL params change
              value={search}
            />
          </div>
          <div className="col-span-1 flex flex-row justify-end gap-4">
            {/* status */}
            <select
              name="status"
              className="select select-bordered w-auto md:w-64"
              onChange={handleStatusChange}
              value={status ?? "ALL"}
            >
              <option value="ALL">All Statuses</option>
              {statuses.length > 0 &&
                statuses.map((status, index) => (
                  <option key={index} value={status}>
                    {status}
                  </option>
                ))}
            </select>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded border-solid border-2">
        <table className="table-normal divide-y-2 bg-white rounded w-full">
          {/* head */}
          <thead className="text-left">
            <tr>
              <th className="text-center">#</th>
              <th>Number</th>
              <th>Reference</th>
              <th>Customer</th>
              <th>Date</th>
              <th className="text-center">Status</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody className="divide-y-2">
            {results &&
              results.map((value, index) => (
                <tr key={value.id}>
                  <th>{(displayPage - 1) * limit + index + 1}</th>
                  <td>
                    <Link to={`/order/${value.id}`}>
                      <span className="font-bold underline">
                        {value.number ? value.number : "-"}
                      </span>
                    </Link>
                  </td>
                  <td>
                    <span>{value.ref}</span>
                  </td>
                  <td className="max-w-sm whitespace-normal">
                    <span>{value.customer.name}</span>
                  </td>
                  <td>
                    <span>
                      {moment(value.date / 1000, "X").format("MMM DD, yy")}
                    </span>
                  </td>
                  <td className="text-center">
                    <span
                      className={`text-xs py-1 px-2 text-white ${statusTagColor(
                        value.status
                      )}`}
                    >
                      {value.status}
                    </span>
                  </td>
                  <td>{formatter.format(value.total / 100)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* table pagination */}
      <div className="flex w-full px-2">
        <div className="flex flex-row justify-between items-center w-full">
          <div>
            <p>
              Showing <span className="font-bold">{from}</span> -{" "}
              <span className="font-bold">{to}</span> of{" "}
              <span className="font-bold">{total}</span> results
            </p>
          </div>
          <div className="btn-group">
            <button
              className="btn btn-outline btn-sm"
              disabled={displayPage === 1 || maxPage <= 1}
              onClick={prevPage}
            >
              Prev
            </button>
            <button
              className="btn btn-outline btn-sm"
              disabled={displayPage >= maxPage || maxPage <= 1}
              onClick={nextPage}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
